import * as React from 'react';
import { useIntl } from 'react-intl';

import { Text, ReactFCC } from '@notino/react-styleguide';

import { partition } from '@utils/array';

import { messages } from '../messages';
import { IConditionalFreeDelivery } from '../model';

import { ConditionsTooltip } from './ConditionsTooltip';
import { ConditionsRow } from './styled';
import { transformToMessageValues } from './utils';

interface IConditionProps {
  targets: IConditionalFreeDelivery['targets'];
}

export const Condition: ReactFCC<IConditionProps> = ({ targets, children }) => {
  const { formatMessage } = useIntl();

  const [brands, categories] = partition(targets, 'type', [
    'Brand',
    'Category',
  ]);

  let formattedMessage;
  if (brands.length > 2 && categories.length > 2) {
    formattedMessage = formatMessage(messages.brandsNCategoriesN, {
      ...transformToMessageValues(brands),
      ...transformToMessageValues(categories),
      these: (parts) => (
        <ConditionsTooltip targets={targets}>{parts}</ConditionsTooltip>
      ),
    });
  } else if (brands.length > 0 && categories.length > 0) {
    const messageId = `brands${brands.length}Categories${categories.length}`;

    formattedMessage = formatMessage(messages[messageId], {
      ...transformToMessageValues(brands),
      ...transformToMessageValues(categories),
      these: (parts) => (
        <ConditionsTooltip targets={targets}>{parts}</ConditionsTooltip>
      ),
    });
  } else if (brands.length > 0) {
    const brandsCount = brands.length > 3 ? 'N' : brands.length;
    const messageId = `brands${brandsCount}Categories0`;

    formattedMessage = formatMessage(messages[messageId], {
      ...transformToMessageValues(brands),
      others: (parts) => (
        <ConditionsTooltip targets={brands.slice(2)}>{parts}</ConditionsTooltip>
      ),
    });
  } else if (categories.length > 0) {
    const categoriesCount = categories.length > 3 ? 'N' : categories.length;
    const messageId = `brands0Categories${categoriesCount}`;

    formattedMessage = formatMessage(messages[messageId], {
      ...transformToMessageValues(categories),
      others: (parts) => (
        <ConditionsTooltip targets={categories.slice(2)}>
          {parts}
        </ConditionsTooltip>
      ),
    });
  } else {
    formattedMessage = formatMessage(messages.brands0Categories0);
  }

  return (
    <ConditionsRow>
      <Text typography="labelSmall400" block={true} marginTop="0.25rem">
        {formattedMessage}
      </Text>

      {children}
    </ConditionsRow>
  );
};
